/**
 * Coding stats API — frontend-only.
 * Tries public APIs; falls back to null so callers use mock data.
 * (Codeforces/LeetCode often block CORS from browsers, so mock is used in practice
 * unless you run a small serverless proxy and point REACT_APP_STATS_PROXY at it.)
 */

const CF_BASE = 'https://codeforces.com/api';
const LC_GRAPHQL = 'https://leetcode.com/graphql';

const LEETCODE_QUERY = `
query userProfile($username: String!) {
  matchedUser(username: $username) {
    username
    profile { ranking }
    submitStatsGlobal {
      acSubmissionNum { difficulty count }
    }
  }
  allQuestionsCount { difficulty count }
}
`;

/**
 * @param {string} handle - Codeforces handle
 * @returns {Promise<object|null>} Same shape as backend /api/stats/codeforces, or null
 */
export async function fetchCodeforcesStats(handle) {
  try {
    const [infoRes, ratingRes] = await Promise.all([
      fetch(`${CF_BASE}/user.info?handles=${encodeURIComponent(handle)}`),
      fetch(`${CF_BASE}/user.rating?handle=${encodeURIComponent(handle)}`),
    ]);
    const infoData = await infoRes.json();
    const ratingData = await ratingRes.json();
    if (infoData?.status !== 'OK' || ratingData?.status !== 'OK') return null;

    const user = infoData.result[0];
    const contests = ratingData.result || [];
    const ratingHistory = contests.map((c) => ({
      contest: c.contestName || '',
      date: new Date(c.ratingUpdateTimeSeconds * 1000).toISOString().slice(0, 10),
      rating: c.newRating,
    }));

    return {
      handle,
      currentRating: user.rating ?? 0,
      maxRating: user.maxRating ?? 0,
      rank: user.rank ?? 'unrated',
      totalSolved: null,
      contestsParticipated: contests.length,
      ratingHistory,
    };
  } catch {
    return null;
  }
}

/**
 * @param {string} username - LeetCode username
 * @returns {Promise<object|null>} Same shape as backend /api/stats/leetcode, or null
 */
export async function fetchLeetCodeStats(username) {
  try {
    const res = await fetch(LC_GRAPHQL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Referer: 'https://leetcode.com' },
      body: JSON.stringify({
        query: LEETCODE_QUERY,
        variables: { username },
      }),
    });
    const data = await res.json();
    const user = data?.data?.matchedUser;
    if (!user) return null;

    const ac = Object.fromEntries(
      (user.submitStatsGlobal?.acSubmissionNum || []).map((o) => [o.difficulty, o.count])
    );
    const totals = (data?.data?.allQuestionsCount || []).reduce(
      (acc, o) => ({ ...acc, [o.difficulty]: o.count }),
      {}
    );

    return {
      handle: username,
      totalSolved: ac.All ?? 0,
      easySolved: ac.Easy ?? 0,
      mediumSolved: ac.Medium ?? 0,
      hardSolved: ac.Hard ?? 0,
      totalQuestions: {
        easy: totals.Easy ?? 800,
        medium: totals.Medium ?? 1700,
        hard: totals.Hard ?? 750,
      },
      ranking: user.profile?.ranking ?? 0,
    };
  } catch {
    return null;
  }
}
